import React, { useEffect, useState } from "react";
import { Link, useLocation } from "@/lib/router";
import { Menu, X } from "lucide-react";
import { ShoppingBag, User, Storefront } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LanguageSelector } from "@/components/LanguageSelector";
import { TEXT } from "@/i18n/content";
import { SHOP } from "@/i18n/shop";
import { splitLang } from "@/lib/i18nPath";

const NavLink = ({ to, label, onClick }) => {
  const { pathname } = useLocation();
  const active = splitLang(pathname).base === to;
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={`nav-link-${to === "/" ? "home" : to.slice(1)}`}
      className={`relative text-sm tracking-wide transition-colors duration-300 ${
        active ? "text-gold" : "text-white/70 hover:text-white"
      }`}
    >
      {label}
      {active && <motion.span layoutId="nav-underline" className="absolute -bottom-1 left-0 h-px w-full bg-gold" />}
    </Link>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const { count, setOpen: setCartOpen } = useCart();
  const { isAdmin } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t(TEXT.nav.home) },
    { to: "/story", label: t(TEXT.nav.about) },
    { to: "/products", label: t(TEXT.nav.products) },
    { to: "/discovery", label: t(TEXT.nav.discovery) },
    { to: "/seasonal", label: t(TEXT.nav.seasonal) },
    { to: "/signature", label: t(TEXT.nav.signature) },
    { to: "/contact", label: t(TEXT.nav.contact) },
    { to: "/founder-club", label: t(TEXT.nav.founderClub) },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 ${open ? "z-[70]" : "z-50"} w-full transition-all duration-500 ${
        scrolled ? "border-b border-white/10 bg-ink/80 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-8">
        <Link to="/" data-testid="brand-logo" className="flex items-center">
          <Logo variant="mark" className="h-9 w-auto sm:h-10" />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} label={l.label} />
          ))}
        </nav>

        <div className="flex items-center gap-5 lg:gap-4">
          <Link
            to="/shop"
            data-testid="navbar-boutique"
            className="hidden items-center gap-2 rounded-full border border-gold/60 px-5 py-2 text-xs uppercase tracking-widest text-gold transition-colors duration-300 hover:bg-gold hover:text-ink sm:inline-flex"
          >
            <Storefront size={16} weight="light" />
            {t(SHOP.nav)}
          </Link>
          <div className="hidden lg:block">
            <LanguageSelector variant="desktop" />
          </div>
          <Link to={isAdmin ? "/admin" : "/account"} data-testid="navbar-account" aria-label="Account" className="-m-2.5 p-2.5 text-white/70 transition-colors hover:text-gold">
            <User size={22} weight="light" />
          </Link>
          <button onClick={() => setCartOpen(true)} data-testid="navbar-cart" aria-label="Cart" className="relative -m-2.5 p-2.5 text-white/70 transition-colors hover:text-gold">
            <ShoppingBag size={22} weight="light" />
            {count > 0 && (
              <span data-testid="navbar-cart-count" className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gold text-[10px] font-medium text-ink">
                {count}
              </span>
            )}
          </button>
          <button className="-m-2.5 p-2.5 text-white lg:hidden" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-white/10 bg-ink/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6 px-6 py-8">
              <Link
                to="/shop"
                onClick={() => setOpen(false)}
                data-testid="mobile-boutique"
                className="flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-xs uppercase tracking-widest text-ink"
              >
                <Storefront size={16} weight="light" />
                {t(SHOP.nav)}
              </Link>
              <div className="h-px w-full bg-white/10" />
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} label={l.label} onClick={() => setOpen(false)} />
              ))}
              <Link to="/account" onClick={() => setOpen(false)} data-testid="mobile-account" className="text-sm text-white/70 hover:text-gold">
                {t(SHOP.account)}
              </Link>
              <LanguageSelector variant="mobile" />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
